import { $t } from '../../composables/i18n'
import { Color } from '../color/Color'
import { CountryId } from '../country/CountryId'
import { Codebook } from '../Codebook'

export enum FiscalisationStatusId {
  NOT_SENT = 0,
  SENT = 1,
  PENDING = 2,
  STORNO = 3,
  PROCESSING = 4,
  AUTO_SYNCED = 5,
  PENDING_STORNO = 6,
  PROCESSING_STORNO = 7,
  AUTO_SENT = 8,
  PARTIALLY_SENT = 9,
  FAILED = 100,
}

export class FiscalisationStatus extends Codebook<FiscalisationStatusId> {
  static readonly NOT_SENT = new FiscalisationStatus(
    FiscalisationStatusId.NOT_SENT,
    () => $t('Not sent'),
    Color.NEGATIVE_STATE,
    'circle-xmark',
    [CountryId.CZE, CountryId.ROU, CountryId.HUN, CountryId.SVK],
  )
  static readonly SENT = new FiscalisationStatus(
    FiscalisationStatusId.SENT,
    () => $t('Sent'),
    Color.POSITIVE_STATE,
    'circle-check',
    [CountryId.CZE, CountryId.ROU, CountryId.HUN, CountryId.SVK],
  )
  static readonly PENDING = new FiscalisationStatus(
    FiscalisationStatusId.PENDING,
    () => $t('Pending'),
    Color.WARNING_STATE,
    'circle-exclamation',
    [CountryId.CZE, CountryId.SVK],
  )
  static readonly STORNO = new FiscalisationStatus(
    FiscalisationStatusId.STORNO,
    () => $t('Canceled'),
    Color.NEGATIVE_STATE,
    'circle-xmark',
    [CountryId.CZE],
  )
  static readonly PROCESSING = new FiscalisationStatus(
    FiscalisationStatusId.PROCESSING,
    () => $t('Processing'),
    Color.WARNING_STATE,
    'clock',
    [CountryId.ROU],
  )
  static readonly AUTO_SYNCED = new FiscalisationStatus(
    FiscalisationStatusId.AUTO_SYNCED,
    () => $t('Auto synced'),
    Color.POSITIVE_STATE,
    'circle-check',
    [],
  )
  static readonly PENDING_STORNO = new FiscalisationStatus(
    FiscalisationStatusId.PENDING_STORNO,
    () => $t('Pending cancel'),
    Color.WARNING_STATE,
    'circle-exclamation',
    [],
  )
  static readonly PROCESSING_STORNO = new FiscalisationStatus(
    FiscalisationStatusId.PROCESSING_STORNO,
    () => $t('Canceling'),
    Color.WARNING_STATE,
    'circle-exclamation',
    [],
  )
  static readonly AUTO_SENT = new FiscalisationStatus(
    FiscalisationStatusId.AUTO_SENT,
    () => $t('Auto sent'),
    Color.POSITIVE_STATE,
    'circle-check',
    [],
  )
  static readonly PARTIALLY_SENT = new FiscalisationStatus(
    FiscalisationStatusId.PARTIALLY_SENT,
    () => $t('Partially sent'),
    Color.WARNING_STATE,
    'circle-exclamation',
    [CountryId.ROU],
  )
  static readonly FAILED = new FiscalisationStatus(
    FiscalisationStatusId.FAILED,
    () => $t('Failed'),
    Color.NEGATIVE_STATE,
    'circle-xmark',
    [CountryId.ROU],
  )

  private constructor(
    public readonly id: FiscalisationStatusId,
    public readonly label: () => string,
    public readonly color: Color.NEGATIVE_STATE | Color.POSITIVE_STATE | Color.WARNING_STATE,
    public readonly icon: string,
    public readonly countryIds: CountryId[],
  ) {
    super(id)
  }

  static getByCountryId = (countryId: CountryId): FiscalisationStatus[] => {
    return FiscalisationStatus.all().filter((item: FiscalisationStatus) =>
      item.countryIds.includes(countryId),
    )
  }

  static isFiscalised = (statusId: FiscalisationStatusId | null): boolean => {
    return (
      statusId === FiscalisationStatusId.SENT ||
      statusId === FiscalisationStatusId.AUTO_SYNCED ||
      statusId === FiscalisationStatusId.AUTO_SENT
    )
  }

  static isEditLocked = (statusId: FiscalisationStatusId | null): boolean => {
    return (
      FiscalisationStatus.isFiscalised(statusId) ||
      FiscalisationStatus.isInProgress(statusId) ||
      statusId === FiscalisationStatusId.PARTIALLY_SENT
    )
  }

  static isInProgress = (statusId: FiscalisationStatusId | null): boolean => {
    return (
      statusId === FiscalisationStatusId.PENDING || statusId === FiscalisationStatusId.PROCESSING
    )
  }
}
